// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { Eye, PhoneIncoming, PhoneOutgoing, Hash } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import { getApiCall } from '@/service/getApiCall';
import { API_NUMBER_CHANNEL_CONSUMPTION } from '@/service/url';
import Loader from '@/components/partials/components/Loader';

function todayIst() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

function emptyHours() {
  return Array.from({ length: 24 }, (_, hour) => ({
    hour,
    label: `${String(hour).padStart(2, '0')}:00`,
    incoming: 0,
    outgoing: 0,
    total: 0,
  }));
}

function formatHourLabel(hour) {
  const h = Number(hour);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${twelve} ${suffix}`;
}

export default function DidChannelConsumptionModal({
  open,
  onClose,
  numberRow,
  displayNumber,
}) {
  const [date, setDate] = useState(todayIst());
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (open) setDate(todayIst());
  }, [open, numberRow?.id]);

  useEffect(() => {
    if (!open || !numberRow?.id || !date) return undefined;
    let cancelled = false;
    setLoading(true);
    const url = `${API_NUMBER_CHANNEL_CONSUMPTION.replace(':id', String(numberRow.id))}?date=${encodeURIComponent(date)}`;
    getApiCall(url)
      .then((resp) => {
        if (!cancelled) setPayload(resp);
      })
      .catch(() => {
        if (!cancelled) setPayload(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, numberRow?.id, date]);

  const hours = payload?.hours?.length === 24 ? payload.hours : emptyHours();
  const totals = payload?.totals || { incoming: 0, outgoing: 0, total: 0 };
  const peak = hours.reduce(
    (best, row) => (row.total > best.total ? row : best),
    hours[0]
  );

  const chartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit' },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: [3, 3, 3] },
      colors: ['#0ea5e9', '#f59e0b', '#10b981'],
      xaxis: {
        categories: hours.map((h) => formatHourLabel(h.hour)),
        labels: { rotate: -45, style: { fontSize: '10px' } },
        title: { text: 'Hour (IST)' },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        title: { text: 'Channels' },
        labels: { formatter: (v) => String(Math.round(Number(v) || 0)) },
      },
      legend: { position: 'top', horizontalAlign: 'left' },
      tooltip: {
        theme: 'dark',
        y: { formatter: (v) => `${Number(v) || 0}` },
      },
      grid: { borderColor: 'rgba(148, 163, 184, 0.25)' },
    }),
    [hours]
  );

  const chartSeries = useMemo(
    () => [
      { name: 'Incoming', data: hours.map((h) => h.incoming) },
      { name: 'Outbound', data: hours.map((h) => h.outgoing) },
      { name: 'Total', data: hours.map((h) => h.total) },
    ],
    [hours]
  );

  const kpis = [
    { label: 'Incoming', value: totals.incoming, Icon: PhoneIncoming, tone: 'text-sky-600 bg-sky-50 dark:bg-sky-950/40' },
    { label: 'Outbound', value: totals.outgoing, Icon: PhoneOutgoing, tone: 'text-amber-600 bg-amber-50 dark:bg-amber-950/40' },
    { label: 'Total', value: totals.total, Icon: Hash, tone: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40' },
  ];

  return (
    <Dialog open={!!open} onOpenChange={(next) => !next && onClose?.()}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
              <Eye className="h-4 w-4" />
            </span>
            DID channel consumption
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Number</p>
            <p className="text-lg font-semibold tabular-nums">{displayNumber || numberRow?.actual_number || '-'}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              One calendar day at a time (IST). Pick a date to load that day&apos;s hourly counts.
            </p>
          </div>
          <div className="w-full sm:w-56">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Date</label>
            <CalendarDateInput
              value={date}
              onChange={setDate}
              max={todayIst()}
              placeholder="Select day"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              contentClassName="z-[200]"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {kpis.map(({ label, value, Icon, tone }) => (
                <div key={label} className="rounded-xl border border-border/70 bg-card p-3">
                  <div className={`mb-2 inline-flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-2xl font-semibold tabular-nums">{value}</p>
                </div>
              ))}
            </div>

            {!payload?.has_data && (
              <p className="rounded-md bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                No consumption recorded for this DID on {date}. Chart shows zeros.
              </p>
            )}

            {payload?.has_data && peak?.total > 0 && (
              <p className="text-sm text-muted-foreground">
                Peak hour: <span className="font-medium text-foreground">{formatHourLabel(peak.hour)}</span>
                {' '}({peak.total} total · {peak.incoming} in · {peak.outgoing} out)
              </p>
            )}

            <div className="rounded-xl border border-border/70 bg-card p-3">
              <h3 className="mb-2 text-sm font-semibold">Hourly channels</h3>
              <Chart type="line" height={280} options={chartOptions} series={chartSeries} />
            </div>

            <div className="overflow-x-auto rounded-xl border border-border/70">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Hour</th>
                    <th className="px-3 py-2 font-medium">Incoming</th>
                    <th className="px-3 py-2 font-medium">Outbound</th>
                    <th className="px-3 py-2 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {hours.map((row) => {
                    const isPeak = payload?.has_data && peak?.total > 0 && row.hour === peak.hour;
                    return (
                      <tr
                        key={row.hour}
                        className={
                          isPeak
                            ? 'bg-emerald-50/80 dark:bg-emerald-950/30'
                            : 'odd:bg-background even:bg-muted/20'
                        }
                      >
                        <td className="px-3 py-1.5 tabular-nums font-medium">
                          {row.label}
                          <span className="ml-1 text-xs text-muted-foreground">{formatHourLabel(row.hour)}</span>
                        </td>
                        <td className="px-3 py-1.5 tabular-nums">{row.incoming}</td>
                        <td className="px-3 py-1.5 tabular-nums">{row.outgoing}</td>
                        <td className="px-3 py-1.5 tabular-nums font-semibold">{row.total}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="border-t bg-muted/40 font-semibold">
                  <tr>
                    <td className="px-3 py-2">Day total</td>
                    <td className="px-3 py-2 tabular-nums">{totals.incoming}</td>
                    <td className="px-3 py-2 tabular-nums">{totals.outgoing}</td>
                    <td className="px-3 py-2 tabular-nums">{totals.total}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
